import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { debounce } from 'lodash';
import SignatureCanvas from 'react-signature-canvas';
import Typography from '../../components/Typography';
import CustomButton from '../../components/CustomButton';
import CustomButtonWithStroke from '../../components/auth/AuthButton';
import CustomModal from '../../components/CustomModal';
import group1 from '../../images/Group1.png';
import group from '../../images/Group.png';
import lightning from '../../images/lightning.png';
import verticalline from '../../images/vertical_line.png';
import signature1 from '../../images/signature1.png';
import '../../styles/Dashboard.css';

// Format date
const formatDate = (dateString) => {
    if (!dateString) return '[Insert Date]';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' });
};

// Format amount
const formatAmount = (amount) => {
    if (!amount) return '[Insert Amount]';
    if (amount >= 1000000) return `$${(amount / 1000000).toFixed(3)}M`;
    if (amount >= 1000) {
        const thousands = amount / 1000;
        return thousands % 1 === 0 ? `$${thousands}K` : `$${(amount / 1000).toFixed(2)}K`;
    }
    return `$${amount.toLocaleString()}`;
};

const ReviewSign = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [user, setUser] = useState(null);
    const [investment, setInvestment] = useState(null);
    const [openLicense, setOpenLicense] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [activeTab, setActiveTab] = useState('draw');
    const [signature, setSignature] = useState(null);
    const [tempSignature, setTempSignature] = useState(null);
    const [openCustomModal, setOpenCustomModal] = useState(false);
    const [customModalType, setCustomModalType] = useState('');
    const [customModalMessage, setCustomModalMessage] = useState('');
    const [customModalTitle, setCustomModalTitle] = useState('');
    const [customModalButtonText, setCustomModalButtonText] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const sigCanvas = useRef();
    const [isContractSigned, setIsContractSigned] = useState(false);
    const [contractTemplate, setContractTemplate] = useState('');
    const handleSignatureUpload = useCallback((e) => {
        const file = e.target.files[0];
        if (file) {
            if (!file.type.startsWith('image/')) {
                setError('Please upload a valid image file (PNG, JPG, etc.).');
                return;
            }
            const reader = new FileReader();
            reader.onloadend = () => setTempSignature(reader.result);
            reader.readAsDataURL(file);
        }
    }, []);

    const handleDrawSignature = () => {
        if (sigCanvas.current) {
            const canvas = sigCanvas.current.getCanvas();
            if (canvas) {
                const context = canvas.getContext('2d');
                // Create a trimmed canvas by checking non-transparent pixels
                const width = canvas.width;
                const height = canvas.height;
                const imageData = context.getImageData(0, 0, width, height);
                let minX = width, minY = height, maxX = 0, maxY = 0;

                for (let y = 0; y < height; y++) {
                    for (let x = 0; x < width; x++) {
                        const i = (y * width + x) * 4;
                        if (imageData.data[i + 3] > 0) { // Check alpha channel for non-transparent pixels
                            minX = Math.min(minX, x);
                            minY = Math.min(minY, y);
                            maxX = Math.max(maxX, x);
                            maxY = Math.max(maxY, y);
                        }
                    }
                }

                if (minX <= maxX && minY <= maxY) {
                    const trimWidth = maxX - minX + 1;
                    const trimHeight = maxY - minY + 1;
                    const trimmedCanvas = document.createElement('canvas');
                    trimmedCanvas.width = trimWidth;
                    trimmedCanvas.height = trimHeight;
                    const trimmedContext = trimmedCanvas.getContext('2d');
                    trimmedContext.drawImage(canvas, minX, minY, trimWidth, trimHeight, 0, 0, trimWidth, trimHeight);
                    setTempSignature(trimmedCanvas.toDataURL('image/png'));
                } else {
                    setTempSignature(null); // No signature drawn
                }
            }
        }
    };


    const handleApplySignature = useCallback(() => {
        if (tempSignature) {
            setSignature(tempSignature);
            setIsModalOpen(false);
            setError(null);
        } else {
            setError('No signature provided. Please draw or upload a signature.');
        }
    }, [tempSignature]);

    const handleReply = useCallback(() => {
        setTempSignature(null);
        if (activeTab === 'draw' && sigCanvas.current) {
            sigCanvas.current.clear();
        }
    }, [activeTab]);

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem('token');
            const [adminResponse, investmentResponse, templateResponse] = await Promise.all([
                axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/auth/admin`, {
                    headers: { Authorization: `Bearer ${token}` },
                    timeout: 10000,
                }),
                axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/investment/${id}`, {
                    headers: { Authorization: `Bearer ${token}` },
                    timeout: 10000,
                }),
                axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/contract/contract-template`, {
                    headers: { Authorization: `Bearer ${token}` },
                    timeout: 10000,
                }),
            ]);
            setInvestment(investmentResponse.data.investment);
            setUser(adminResponse.data.users.find((u) => u._id === investmentResponse.data.investment.userId));
            setContractTemplate(templateResponse.data.template);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch data. Please try again.');
        } finally {
            setIsLoading(false);
        }
    }, [id]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleDecline = useCallback(
        debounce(async () => {
            setIsLoading(true);
            try {
                const token = localStorage.getItem('token');
                const res = await axios.put(
                    `${process.env.REACT_APP_BACKEND_URL}/api/investment/decline/${id}`,
                    {},
                    { headers: { Authorization: `Bearer ${token}` }, timeout: 10000 }
                );
                if (res.data.success) {
                    setOpenCustomModal(true);
                    setCustomModalType('success');
                    setCustomModalTitle('INVESTOR DECLINED!');
                    setCustomModalMessage(res.data.message);
                    setCustomModalButtonText('RETURN TO DASHBOARD');
                }
            } catch (err) {
                setError(err.response?.data?.message || 'Failed to decline investor. Please try again.');
            } finally {
                setIsLoading(false);
            }
        }, 300),
        [id]
    );

    const downloadPDF = useCallback(async () => {
        const input = document.getElementById('Content_View_Contract');
        if (!input) {
            setError('Contract content not found. Please try again.');
            return;
        }

        setIsLoading(true);
        try {
            const headers = input.querySelectorAll('header');
            headers.forEach((header) => header.classList.add('pdf-header-adjust'));
            document.getElementById('replace_signature_button').style.display = 'none';

            const { default: html2canvas } = await import('html2canvas');
            const { jsPDF } = await import('jspdf');
            const canvas = await html2canvas(input, {
                scale: 2,
                useCORS: true,
                backgroundColor: '#121212',
            });
            const imgData = canvas.toDataURL('image/png');
            const pdf = new jsPDF({
                orientation: 'portrait',
                unit: 'px',
                format: [canvas.width, canvas.height],
            });
            pdf.addImage(imgData, 'PNG', 0, 0, canvas.width, canvas.height);
            pdf.save(`contract_${new Date().toISOString()}.pdf`);
            headers.forEach((header) => header.classList.remove('pdf-header-adjust'));
            document.getElementById('replace_signature_button').style.display = 'flex';
        } catch (err) {
            setError('Failed to generate PDF. Please try again.');
        } finally {
            setIsLoading(false);
        }
    }, []);

    const handleApprove = useCallback(
        debounce(async () => {
            if (investment?.status === 'active') {
                setOpenCustomModal(true);
                setCustomModalType('error');
                setCustomModalTitle('ALREADY APPROVED');
                setCustomModalMessage('This investment has already been approved.');
                setCustomModalButtonText('RETURN TO DASHBOARD');
                return;
            }
            if (!signature) {
                setOpenCustomModal(true);
                setCustomModalType('error');
                setCustomModalTitle('SIGNATURE REQUIRED');
                setCustomModalMessage('Please add your signature before approving the investment.');
                setCustomModalButtonText('OKAY');
                return;
            }
            setIsLoading(true);
            try {
                const token = localStorage.getItem('token');
                const res = await axios.put(
                    `${process.env.REACT_APP_BACKEND_URL}/api/investment/approve/${id}`,
                    { signature },
                    { headers: { Authorization: `Bearer ${token}` }, timeout: 10000 }
                );
                console.log(res);
                if (res.data.success) {
                    await downloadPDF();
                    setOpenCustomModal(true);
                    setCustomModalType('success');
                    setCustomModalTitle('CONTRACT SIGNED!');
                    setCustomModalMessage(res.data.message);
                    setCustomModalButtonText('RETURN TO DASHBOARD');
                    setIsContractSigned(true);
                }
            } catch (err) {
                setError(err.response?.data?.message || 'Failed to approve investment. Please try again.');
            } finally {
                setIsLoading(false);
            }
        }, 300),
        [id, signature, downloadPDF, investment?.status]
    );

    // Function to replace placeholders with actual values
    const renderContract = () => {
        if (!contractTemplate) return '<p>Loading contract...</p>';
        return contractTemplate
            .replace(/\[Insert Start Date\]/g, formatDate(investment?.startDate))
            .replace(/\[Insert End Date\]/g, formatDate(investment?.endDate))
            .replace(/\[Investor Full Legal Name\]/g, user?.name || '[Name]')
            .replace(/\[Investor Address\]/g, user?.address || '[Address]')
            .replace(/\[Insert Amount\]/g, formatAmount(investment?.initialInvestment))
            .replace(/\[Insert Target Amount\]/g, formatAmount((investment?.initialInvestment || 240000) * (investment?.roiPercentage || 20) / 100 || 65000))
            .replace(/\[1-Year \| 2\.5-Year Semi-Annual \| 2\.5-Year Lump Sum\]/g, investment?.plan || '[Plan]')
            .replace(/\[Quarterly \| Semi-Annually \| Lump Sum\]/g, investment?.payoutSchedule || 'Semi-Annually')
            .replace(/\[Status\]/g, investment?.status === 'wireConfirmed' ? 'Wire Transfer Pending (1-5 days)' : investment?.status || 'Pending');
    };

    // console.log(renderContract());

    return (
        <div className="lg:py-[72px] lg:px-[93px] md:py-[17px] md:px-[60px] py-[30px] px-4 w-full h-full min-h-screen lg:bg-[#121212] bg-[#0F0F0F]">
            {(isLoading || error) && (
                <CustomModal
                    type={error ? 'error' : 'loading'}
                    title={error ? 'Error' : 'Please wait'}
                    message={error || 'Please wait while we process your request...'}
                    confirmButton={!!error}
                    onClose={() => setError(null)}
                    colseText={error ? 'Try Again' : undefined}
                />
            )}
            {openLicense && (
                <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50" onClick={() => setOpenLicense(false)}>
                    <div className="bg-[#121212] p-4 rounded-lg max-w-4xl max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
                        <img src={user?.license ? `${process.env.REACT_APP_BACKEND_URL}${user?.license}` : ''} alt="Preview" className="max-w-full max-h-[80vh]" />
                        {/* <button onClick={() => setShowPreviewModal(false)} className="mt-4 bg-red-500 text-white px-4 py-2 rounded">Close</button> */}
                    </div>
                </div>
            )}
            {/* {openLicense && (
                <div className="fixed fade inset-0 z-50 flex items-center h-full justify-center bg-black bg-opacity-60 animate-backdropFadeIn">
                    <div className="relative md:w-[661px] max-w-4xl max-h-[90vh] mx-auto animate-modalSlideIn">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="md:block hidden"
                            width="661"
                            height="404"
                            viewBox="0 0 661 404"
                            fill="none"
                        >
                            <path d="M0 53.5L58.5 0H661V404H0V53.5Z" fill="#1B1B1B" />
                        </svg>
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="md:hidden block"
                            width="333"
                            height="289"
                            viewBox="0 0 333 289"
                            fill="none"
                        >
                            <path d="M0 53.5L58.5 0H333V289H0V53.5Z" fill="#1B1B1B" />
                        </svg>
                        <div className="absolute inset-0 flex flex-col py-[42px] px-[66px] items-center justify-between text-white text-center">
                            <button
                                onClick={() => setOpenLicense(false)}
                                className="absolute top-6 right-6 text-white text-lg hover:text-gray-300"
                                aria-label="Close modal"
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="30"
                                    height="30"
                                    viewBox="0 0 30 30"
                                    fill="none"
                                >
                                    <g opacity="0.6">
                                        <mask
                                            id="mask0_1769_5697"
                                            style={{ maskType: 'alpha' }}
                                            maskUnits="userSpaceOnUse"
                                            x="0"
                                            y="0"
                                            width="30"
                                            height="30"
                                        >
                                            <rect width="30" height="30" fill="#D9D9D9" />
                                        </mask>
                                        <g mask="url(#mask0_1769_5697)">
                                            <path
                                                d="M8.00078 23.317L6.68359 21.9998L13.6836 14.9998L6.68359 7.99981L8.00078 6.68262L15.0008 13.6826L22.0008 6.68262L23.318 7.99981L16.318 14.9998L23.318 21.9998L22.0008 23.317L15.0008 16.317L8.00078 23.317Z"
                                                fill="white"
                                            />
                                        </g>
                                    </g>
                                </svg>
                            </button>
                            <img
                                className="my-auto"
                                alt="driver_license"
                                src={user?.license ? `${process.env.REACT_APP_BACKEND_URL}${user?.license}` : ''}
                            />
                        </div>
                    </div>
                </div>
            )} */}
            {openCustomModal && (
                <CustomModal
                    type={customModalType}
                    title={customModalTitle}
                    message={customModalMessage}
                    onClose={() => setOpenCustomModal(false)}
                    onConfirm={() => {
                        if (customModalTitle === 'CONTRACT SIGNED!' || customModalTitle === 'INVESTOR DECLINED!' || customModalTitle === 'ALREADY APPROVED') {
                            navigate('/admin/investments');
                        }
                        if (customModalTitle === 'DECLINE INVESTOR?') {
                            handleDecline();
                        }
                        if (customModalTitle === 'ARE YOU SURE?') {
                            handleApprove();
                        }
                        setOpenCustomModal(false);
                    }}
                    colseText={customModalButtonText}
                    cancelButton={customModalType === 'confirm'}
                />
            )}
            <div className="flex justify-between items-center">
                <button onClick={() => navigate(-1)}>
                    <Typography
                        color="#FFF"
                        size="18.462px"
                        family="Supply"
                        weight={400}
                        lineheight="130%"
                        text={
                            <>
                                <span className="text-[#DF0F11]">&gt;</span> <span>GO BACK</span>
                            </>
                        }
                    />
                </button>
                <div className="md:flex hidden items-center">
                    <div className="flex relative justify-center items-center">
                        <img src={group1} alt="group1" />
                        <img
                            src={lightning}
                            alt="lightning"
                            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
                        />
                    </div>
                    <img src={verticalline} alt="verticalline" className="ml-[19.26px]" />
                    <img src={group} alt="group" className="ml-[16.53px]" />
                </div>
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="md:hidden block"
                    width="139"
                    height="23"
                    viewBox="0 0 139 23"
                    fill="none"
                >
                    <g clipPath="url(#clip0_4663_16331)">
                        <path
                            d="M64.2662 12.4248C64.0709 12.9891 63.7409 13.4313 63.277 13.7513C62.813 14.0713 62.2645 14.2326 61.6313 14.2326C60.7899 14.2326 60.1 13.9619 59.5589 13.4212C59.0169 12.8806 58.7482 12.1878 58.7482 11.3427C58.7482 10.4976 59.0169 9.80565 59.5589 9.26413C60.101 8.72352 60.7899 8.45276 61.6313 8.45276C62.2468 8.45276 62.7823 8.60136 63.2425 8.90494C63.7 9.20487 64.03 9.62058 64.2281 10.1521H65.1866C64.9682 9.35712 64.5405 8.72717 63.898 8.25493C63.2556 7.7827 62.4988 7.54932 61.6304 7.54932C60.8968 7.54932 60.2376 7.71068 59.6519 8.03431C59.0662 8.35795 58.6059 8.80922 58.2759 9.3872C57.9458 9.96428 57.7812 10.6179 57.7812 11.3436C57.7812 12.0693 57.9458 12.7193 58.2759 13.3C58.6059 13.8771 59.0662 14.3293 59.6519 14.6529C60.2376 14.9765 60.8968 15.1379 61.6304 15.1379C62.5183 15.1379 63.2853 14.8936 63.9315 14.4058C64.5777 13.9181 65.0081 13.2581 65.2238 12.4266H64.2653L64.2662 12.4248Z"
                            fill="white"
                        />
                        <path
                            d="M75.9721 7.66064H75.0907L72.0254 15.024H73.0249L73.7584 13.2627H77.3649L78.1087 15.024H79.1286L75.9731 7.66064H75.9721ZM74.108 12.4149L75.5482 8.93422L77.0153 12.4149H74.108Z"
                            fill="white"
                        />
                        <path
                            d="M91.2398 8.33709C90.7619 7.88491 90.1697 7.66064 89.4631 7.66064H86.7715V15.024H87.7068V12.3784H89.4631C90.1734 12.3784 90.7684 12.1542 91.2426 11.7056C91.7168 11.2571 91.9529 10.6964 91.9529 10.0227C91.9529 9.34902 91.714 8.78562 91.2398 8.33709ZM90.5462 11.0693C90.2496 11.3464 89.8703 11.485 89.4092 11.485H87.7068V8.55588H89.4092C89.8703 8.55588 90.2506 8.69445 90.5462 8.97524C90.8419 9.25238 90.9906 9.60246 90.9906 10.0246C90.9906 10.4466 90.8428 10.7931 90.5462 11.0702V11.0693Z"
                            fill="white"
                        />
                        <path
                            d="M99.7422 7.66064V15.024H100.681V7.66064H99.7422Z"
                            fill="white"
                        />
                        <path
                            d="M108.365 7.66064V8.52854H110.647V15.024H111.589V8.52854H113.873V7.66064H108.365Z"
                            fill="white"
                        />
                        <path
                            d="M123.966 7.66064H123.085L120.017 15.024H121.019L121.749 13.2627H125.359L126.103 15.024H127.123L123.967 7.66064H123.966ZM122.102 12.4149L123.542 8.93422L125.006 12.4149H122.102Z"
                            fill="white"
                        />
                        <path
                            d="M135.702 14.1598V7.66064H134.767V15.024H138.999V14.1598H135.702Z"
                            fill="white"
                        />
                        <path
                            d="M7.79216 5.99487H5.12101L0 18.8218H2.99098L3.88632 16.384H9.10123L10.0161 18.8218H13.0815L7.79216 5.99487ZM4.78445 13.9654L6.48401 9.38256L8.2031 13.9654H4.78445Z"
                            fill="white"
                        />
                        <path
                            d="M35.1163 5.99487V10.9406H30.2045V5.99487H27.3613V18.8218H30.2045V13.5233H35.1163V18.8218H37.939V5.99487H35.1163Z"
                            fill="white"
                        />
                        <path
                            d="M49.112 0H48.04V22.6846H49.112V0Z"
                            fill="white"
                        />
                        <path
                            d="M19.5407 10.5132L16.9607 9.79299C19.0674 8.27875 21.2412 6.7335 23.3489 5.22016C21.8409 6.67424 20.2891 8.19123 18.782 9.64531C18.7857 9.65807 18.7904 9.67083 18.7941 9.68269C19.7229 9.958 20.6508 10.2333 21.5908 10.5114C22.1384 9.60702 22.6832 8.70813 23.2271 7.80833C23.771 6.90763 24.3168 6.00783 24.8588 5.10529C25.1284 4.65676 25.4064 4.19729 25.6826 3.73691C25.9596 3.27744 26.2358 2.81705 26.5026 2.3667C24.2424 3.88277 21.9552 5.41525 19.669 6.94774C17.3818 8.47931 15.0956 10.0118 12.8345 11.5288C15.104 12.1451 17.4255 12.7686 19.722 13.3922C18.8992 14.2181 18.0615 15.0532 17.2247 15.8865C16.3879 16.7197 15.553 17.5511 14.7376 18.3698C14.9943 18.0288 15.3011 17.6113 15.5577 17.2703C15.8189 16.923 16.0802 16.5757 16.3414 16.2283C16.5999 15.8846 16.8612 15.5419 17.1159 15.1954C17.3688 14.8526 17.6264 14.4889 17.869 14.1379C16.5739 15.6048 15.2629 17.0862 13.9502 18.5685C12.6383 20.05 11.3264 21.5323 10.0322 22.9991C15.3503 19.4847 20.7373 15.9384 26.0796 12.4085C26.1047 12.3921 26.1298 12.3757 26.1549 12.3584L19.5416 10.5123H19.5398L19.5407 10.5132Z"
                            fill="#EE0002"
                        />
                    </g>
                    <defs>
                        <clipPath id="clip0_4663_16331">
                            <rect width="139" height="23" fill="white" />
                        </clipPath>
                    </defs>
                </svg>
                <CustomButtonWithStroke
                    onClick={() => {
                        setOpenCustomModal(true);
                        setCustomModalType('confirm');
                        setCustomModalTitle('DECLINE INVESTOR?');
                        setCustomModalMessage('Are you sure you want to decline this investor?');
                        setCustomModalButtonText('YES CORRECT');
                    }}
                    text={
                        <Typography
                            text={
                                <>
                                    <span className="text-[#DF0F11]">X </span>
                                    <span>DECLINE CLIENT</span>
                                </>
                            }
                            size="16px"
                            family="Supply"
                            weight={400}
                            lineheight="170%"
                            transform="uppercase"
                        />
                    }
                    className="text-white md:block hidden w-[209px] h-12"
                />
            </div>
            <div id="Content_View_Contract">
                <div className="lg:mt-[97px] md:mt-[66px] mt-[30px] max-w-[712px] mx-auto">
                    <Typography
                        color="#FFF"
                        size="24px"
                        family="Supply"
                        weight={400}
                        lineheight="130%"
                        text="AGREEMENT DETAILS"
                    />
                    <Typography
                        className="mt-5"
                        color="#FFF"
                        opacity={0.6}
                        size="14px"
                        weight={400}
                        lineheight="150%"
                        text="This document outlines the terms and conditions of your investment."
                    />
                    {investment?.status === 'active' && (
                        <Typography
                            className="mt-5"
                            color="#FFF"
                            size="14px"
                            weight={400}
                            lineheight="150%"
                            text="This investment has already been approved."
                        />
                    )}
                    <div className="mt-9 border-[#262626] border-[1px]">
                        <header className="h-[51px] px-6 flex items-center bg-[#333]">
                            <Typography
                                color="#FFF"
                                size="16px"
                                family="Supply"
                                weight={400}
                                lineheight="170%"
                                transform="uppercase"
                                text="INVESTOR INFO"
                            />
                        </header>
                        <div className="px-6 py-5">
                            <div className="flex sm:flex-col flex-row justify-between self-stretch gap-4">
                                <div className="sm:grid sm:grid-cols-3 flex flex-col gap-4">
                                    <div className="col-span-1">
                                        <Typography
                                            color="#FFF"
                                            size="12px"
                                            opacity={0.6}
                                            weight={400}
                                            lineheight="170%"
                                            text="Full Name"
                                        />
                                        <Typography
                                            className="mt-3"
                                            color="#FFF"
                                            size="14px"
                                            weight={400}
                                            lineheight="170%"
                                            text={user?.name}
                                        />
                                    </div>
                                    <div className="col-span-1">
                                        <Typography
                                            className="truncate"
                                            color="#FFF"
                                            size="12px"
                                            opacity={0.6}
                                            weight={400}
                                            lineheight="170%"
                                            text="Email Address"
                                        />
                                        <Typography
                                            className="mt-3 break-all sm:break-normal"
                                            color="#FFF"
                                            size="14px"
                                            weight={400}
                                            lineheight="170%"
                                            text={user?.email}
                                        />
                                    </div>
                                    <div className="col-span-1">
                                        <Typography
                                            color="#FFF"
                                            size="12px"
                                            opacity={0.6}
                                            weight={400}
                                            lineheight="170%"
                                            text="Phone Number"
                                        />
                                        <Typography
                                            className="mt-3"
                                            color="#FFF"
                                            size="14px"
                                            weight={400}
                                            lineheight="170%"
                                            text={user?.phone}
                                        />
                                    </div>
                                </div>
                                <div className="sm:grid sm:grid-cols-3 flex flex-col gap-4 sm:mt-9">
                                    <div className="col-span-1">
                                        <Typography
                                            color="#FFF"
                                            size="12px"
                                            opacity={0.6}
                                            weight={400}
                                            lineheight="170%"
                                            text="Occupancy"
                                        />
                                        <Typography
                                            className="mt-3"
                                            color="#FFF"
                                            size="14px"
                                            weight={400}
                                            lineheight="170%"
                                            text={user?.occupancy}
                                        />
                                    </div>
                                    <div className="col-span-1">
                                        <Typography
                                            color="#FFF"
                                            size="12px"
                                            opacity={0.6}
                                            weight={400}
                                            lineheight="170%"
                                            text="Instagram"
                                        />
                                        <Typography
                                            className="mt-3"
                                            color="#FFF"
                                            size="14px"
                                            weight={400}
                                            lineheight="170%"
                                            text={user?.instagram}
                                        />
                                    </div>
                                    <div className="col-span-1">
                                        <Typography
                                            color="#FFF"
                                            size="12px"
                                            opacity={0.6}
                                            weight={400}
                                            lineheight="170%"
                                            text="Gender"
                                        />
                                        <Typography
                                            className="mt-3"
                                            color="#FFF"
                                            size="14px"
                                            weight={400}
                                            lineheight="170%"
                                            text={user?.gender}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="w-full mt-9">
                                <Typography
                                    color="#FFF"
                                    size="12px"
                                    opacity={0.6}
                                    weight={400}
                                    lineheight="170%"
                                    text="Investor Bio"
                                />
                                <Typography
                                    className="mt-3"
                                    color="#FFF"
                                    size="14px"
                                    weight={400}
                                    lineheight="170%"
                                    text={user?.bio}
                                />
                            </div>
                            <div className="grid grid-cols-3 mt-9">
                                <div className="col-span-1">
                                    <Typography
                                        color="#FFF"
                                        size="12px"
                                        opacity={0.6}
                                        weight={400}
                                        lineheight="170%"
                                        text="Investor ID/License"
                                    />
                                    <div
                                        onClick={() => setOpenLicense(true)}
                                        className="flex cursor-pointer mt-4 flex-col justify-center items-center rounded-[6px] w-[161px] h-[97px] border-[1px] border-[#2A2A2A]"
                                        style={{
                                            background: `linear-gradient(0deg, rgba(0, 0, 0, 0.90) 0%, rgba(0, 0, 0, 0.90) 100%), url(${user?.license ? `${process.env.REACT_APP_BACKEND_URL}${user?.license}` : ''
                                                }) lightgray 50% / cover no-repeat`,
                                        }}
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                        >
                                            <path
                                                d="M15 12C15 13.6569 13.6569 15 12 15C10.3431 15 9 13.6569 9 12C9 10.3431 10.3431 9 12 9C13.6569 9 15 10.3431 15 12Z"
                                                fill="#DF0F11"
                                            />
                                            <path
                                                d="M21.8944 11.5528C19.7362 7.23635 15.9031 5 12 5C8.09687 5 4.26379 7.23635 2.10557 11.5528C1.96481 11.8343 1.96481 12.1657 2.10557 12.4472C4.26379 16.7637 8.09687 19 12 19C15.9031 19 19.7362 16.7637 21.8944 12.4472C22.0352 12.1657 22.0352 11.8343 21.8944 11.5528ZM12 17C9.03121 17 5.99806 15.3792 4.12966 12C5.99806 8.62078 9.03121 7 12 7C14.9688 7 18.0019 8.62078 19.8703 12C18.0019 15.3792 14.9688 17 12 17Z"
                                                fill="#DF0F11"
                                            />
                                        </svg>
                                        <Typography
                                            size="8.293px"
                                            family="Supply"
                                            text="VIEW LICENSE"
                                            color="#DF0F11"
                                            lineheight="1"
                                            weight={400}
                                            transform="uppercase"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="mt-5 border-[#262626] border-[1px]">
                        <header className="h-[51px] px-6 flex items-center bg-[#333]">
                            <Typography
                                color="#FFF"
                                size="16px"
                                family="Supply"
                                weight={400}
                                lineheight="170%"
                                transform="uppercase"
                                text="INVESTMENT INFO"
                            />
                        </header>
                        <div className="px-6 py-5">
                            <div className="grid grid-cols-3">
                                <div className="col-span-1">
                                    <Typography
                                        color="#FFF"
                                        size="12px"
                                        opacity={0.6}
                                        weight={400}
                                        lineheight="170%"
                                        text="Investment Amount"
                                    />
                                    <Typography
                                        className="mt-3"
                                        color="#FFF"
                                        size="14px"
                                        weight={400}
                                        lineheight="170%"
                                        text={formatAmount(investment?.initialInvestment || 240000)}
                                    />
                                </div>
                                <div className="col-span-1">
                                    <Typography
                                        color="#FFF"
                                        size="12px"
                                        opacity={0.6}
                                        weight={400}
                                        lineheight="170%"
                                        text="Investment Term"
                                    />
                                    <Typography
                                        className="mt-3"
                                        color="#FFF"
                                        size="14px"
                                        weight={400}
                                        lineheight="170%"
                                        text={`${investment?.term || 6} months`}
                                    />
                                </div>
                                <div className="col-span-1">
                                    <Typography
                                        color="#FFF"
                                        size="12px"
                                        opacity={0.6}
                                        weight={400}
                                        lineheight="170%"
                                        text="Target ROI"
                                    />
                                    <Typography
                                        className="mt-3"
                                        color="#FFF"
                                        size="14px"
                                        weight={400}
                                        lineheight="170%"
                                        text={formatAmount(
                                            (investment?.initialInvestment || 240000) * (investment?.roiPercentage || 20) / 100 || 65000
                                        )}
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-3 mt-9">
                                <div className="col-span-1">
                                    <Typography
                                        color="#FFF"
                                        size="12px"
                                        opacity={0.6}
                                        weight={400}
                                        lineheight="170%"
                                        text="Start Date"
                                    />
                                    <Typography
                                        className="mt-3"
                                        color="#FFF"
                                        size="14px"
                                        weight={400}
                                        lineheight="170%"
                                        text={formatDate(investment?.startDate || '2024-09-18')}
                                    />
                                </div>
                                <div className="col-span-1">
                                    <Typography
                                        color="#FFF"
                                        size="12px"
                                        opacity={0.6}
                                        weight={400}
                                        lineheight="170%"
                                        text="End Date"
                                    />
                                    <Typography
                                        className="mt-3"
                                        color="#FFF"
                                        size="14px"
                                        weight={400}
                                        lineheight="170%"
                                        text={formatDate(investment?.endDate || '2025-03-18')}
                                    />
                                </div>
                                <div className="col-span-1">
                                    <Typography
                                        color="#FFF"
                                        size="12px"
                                        opacity={0.6}
                                        weight={400}
                                        lineheight="170%"
                                        text="ROI Percentage"
                                    />
                                    <Typography
                                        className="mt-3"
                                        color="#FFF"
                                        size="14px"
                                        weight={400}
                                        lineheight="170%"
                                        text={`${investment?.roiPercentage || 20}%`}
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-3 mt-9">
                                <div className="col-span-1">
                                    <Typography
                                        color="#FFF"
                                        size="12px"
                                        opacity={0.6}
                                        weight={400}
                                        lineheight="170%"
                                        text="Status"
                                    />
                                    <Typography
                                        className="mt-3"
                                        color="#FFF"
                                        size="14px"
                                        weight={400}
                                        lineheight="170%"
                                        text={
                                            investment?.status === 'wire_transfer_pending'
                                                ? 'Wire Transfer Pending (1-5 days)'
                                                : investment?.status || 'Pending'
                                        }
                                        title={
                                            investment?.status === 'wire_transfer_pending'
                                                ? 'Wire transfers take 1-5 business days to confirm'
                                                : undefined
                                        }
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="mt-5 border-[#262626] border-[1px]">
                        <header className="h-[51px] px-6 flex items-center bg-[#333]">
                            <Typography
                                color="#FFF"
                                size="16px"
                                family="Supply"
                                weight={400}
                                lineheight="170%"
                                transform="uppercase"
                                text="CONTRACT DETAILS"
                            />
                        </header>
                        <div className="px-[30px] py-[31px] relative">
                            <div
                                className="w-full whitespace-pre-wrap text-white text-left flex"
                                style={{ fontFamily: 'Supply', fontSize: '10px', fontStyle: 'normal', lineHeight: '15px', textTransform: 'uppercase' }}
                            >
                                {renderContract()}
                            </div>
                            {/* <textarea
                                value={renderContract()}
                                readOnly
                                disabled
                                rows={50}
                                style={{ fontFamily: 'Supply', fontSize: '10px', fontStyle: 'normal', lineHeight: '15px', textTransform: 'uppercase' }}
                                className="w-full bg-transparent  whitespace-pre-wrap text-left flex scrollbar-hide scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-transparent overflow-y-auto overflow-x-auto p-5 text-white"
                            /> */}
                            <div
                                className="mt-[42px] px-[95px] h-[1px]"
                                style={{ background: 'linear-gradient(90deg, #000 0%, #DF0F11 49.5%, #000 100%)' }}
                            />
                            <div className="mt-7">
                                <div className="flex md:flex-row gap-10 flex-col justify-between items-center">
                                    <div className="flex flex-col max-md:items-center justify-center">
                                        <img
                                            alt="client_signature"
                                            className="w-[146px] h-[54.75px] mx-auto"
                                            src={investment?.signature || signature1}
                                        />
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="mt-[7.31px]"
                                            width="191"
                                            height="2"
                                            viewBox="0 0 191 2"
                                            fill="none"
                                        >
                                            <path opacity="0.4" d="M191 1.375H0" stroke="white" />
                                        </svg>
                                        <Typography
                                            className="mt-[15.63px] text-center"
                                            text="*Client Signature"
                                            color="#666"
                                            size="12px"
                                        />
                                    </div>
                                    <div className="flex flex-col max-md:items-center justify-center">
                                        {signature && (
                                            <div className="relative">
                                                <img
                                                    src={signature}
                                                    alt="Signature"
                                                    className="max-h-[54.75px] mx-auto mb-2 max-w-[190px]"
                                                />
                                            </div>
                                        )}
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="mt-[7.31px]"
                                            width="191"
                                            height="2"
                                            viewBox="0 0 191 2"
                                            fill="none"
                                        >
                                            <path opacity="0.4" d="M191 1.375H0" stroke="white" />
                                        </svg>
                                        <button
                                            onClick={() => setIsModalOpen(true)}
                                            id="replace_signature_button"
                                            style={{ padding: '10px 10px 10px 16px' }}
                                            className="text-white gap-[21px] inline-flex mt-5 h-8 items-center justify-center flex-shrink-0 rounded bg-[#DF0F11]"
                                        >
                                            <Typography
                                                size="12px"
                                                weight={500}
                                                color="#FFF"
                                                lineheight="16px"
                                                text={signature ? 'Replace Signature' : 'Add your signature'}
                                            />
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="20"
                                                height="21"
                                                viewBox="0 0 20 21"
                                                fill="none"
                                            >
                                                <mask
                                                    id="mask0_579_617"
                                                    style={{ maskType: 'alpha' }}
                                                    maskUnits="userSpaceOnUse"
                                                    x="0"
                                                    y="0"
                                                    width="20"
                                                    height="21"
                                                >
                                                    <rect y="0.375" width="20" height="20" fill="#D9D9D9" />
                                                </mask>
                                                <g mask="url(#mask0_579_617)">
                                                    <path
                                                        d="M9.375 13.5321V6.53208L7.32063 8.58646L6.44229 7.68271L10 4.125L13.5577 7.68271L12.6794 8.58646L10.625 6.53208V13.5321H9.375ZM5.25646 16.625C4.83549 16.625 4.47917 16.4792 4.1875 16.1875C3.89583 15.8958 3.75 15.5395 3.75 15.1185V12.859H5V15.1185C5 15.1827 5.02674 15.2415 5.08021 15.2948C5.13354 15.3483 5.19229 15.375 5.25646 15.375H14.7435C14.8077 15.375 14.8665 15.3483 14.9198 15.2948C14.9733 15.2415 15 15.1827 15 15.1185V12.859H16.25V15.1185C16.25 15.5395 16.1042 15.8958 15.8125 16.1875C15.5208 16.4792 15.1645 16.625 14.7435 16.625H5.25646Z"
                                                        fill="white"
                                                    />
                                                </g>
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <CustomButton
                onClick={() => {
                    setOpenCustomModal(true);
                    setCustomModalTitle('ARE YOU SURE?');
                    setCustomModalMessage('Are you sure you want to approve this client as an investor? If so, click "Approve".');
                    setCustomModalType('confirm');
                    setCustomModalButtonText('APPROVE CLIENT');
                }}
                className="mt-10 h-12 md:w-72 m-auto"
                text={investment?.status === 'active' ? "Completed" : investment?.status === 'pending' ? "APPROVE & DOWNLOAD PDF" : investment?.status === 'approved' ? "Approved" : investment?.status === 'wireConfirmed' ?? "Wire Confirmed"}
                disabled={investment?.status === 'active' || investment?.status === 'approved' || isContractSigned}
            />
            <CustomButtonWithStroke
                onClick={() => {
                    setOpenCustomModal(true);
                    setCustomModalType('confirm');
                    setCustomModalTitle('DECLINE INVESTOR?');
                    setCustomModalMessage('Are you sure you want to decline this investor?');
                    setCustomModalButtonText('YES CORRECT');
                }}
                text={
                    <Typography
                        text={
                            <>
                                <span className="text-[#DF0F11]">X </span>
                                <span>DECLINE CLIENT</span>
                            </>
                        }
                        size="16px"
                        family="Supply"
                        weight={400}
                        lineheight="170%"
                        transform="uppercase"
                    />
                }
                className="text-white md:hidden block mt-10 md:w-[209px] h-12"
            />
            {isModalOpen && (
                <div className="fixed inset-0 animate-backdropFadeIn bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-[#1B1B1B] p-6 rounded-lg relative w-[400px]">
                        <button
                            onClick={() => setIsModalOpen(false)}
                            className="absolute top-2 right-2 text-white text-lg hover:text-gray-300"
                            aria-label="Close modal"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="30"
                                height="30"
                                viewBox="0 0 30 30"
                                fill="none"
                            >
                                <g opacity="0.6">
                                    <mask
                                        id="mask0_1769_5697"
                                        style={{ maskType: 'alpha' }}
                                        maskUnits="userSpaceOnUse"
                                        x="0"
                                        y="0"
                                        width="30"
                                        height="30"
                                    >
                                        <rect width="30" height="30" fill="#D9D9D9" />
                                    </mask>
                                    <g mask="url(#mask0_1769_5697)">
                                        <path
                                            d="M8.00078 23.317L6.68359 21.9998L13.6836 14.9998L6.68359 7.99981L8.00078 6.68262L15.0008 13.6826L22.0008 6.68262L23.318 7.99981L16.318 14.9998L23.318 21.9998L22.0008 23.317L15.0008 16.317L8.00078 23.317Z"
                                            fill="white"
                                        />
                                    </g>
                                </g>
                            </svg>
                        </button>
                        <div className="flex border-b border-[#262626] mb-4">
                            <button
                                className={`px-4 py-2 ${activeTab === 'draw' ? 'border-b-2 border-red-700 text-white' : 'text-gray-400'}`}
                                onClick={() => setActiveTab('draw')}
                            >
                                <Typography text="Draw" color="#FFF" size="14px" weight={500} />
                            </button>
                            <button
                                className={`px-4 py-2 ${activeTab === 'upload' ? 'border-b-2 border-red-700 text-white' : 'text-gray-400'}`}
                                onClick={() => setActiveTab('upload')}
                            >
                                <Typography text="Upload" color="#FFF" size="14px" weight={500} />
                            </button>
                        </div>
                        {activeTab === 'upload' && (
                            <div>
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleSignatureUpload}
                                    className="mb-4 text-white"
                                />
                                {tempSignature && (
                                    <div className="relative text-center">
                                        <img
                                            src={tempSignature}
                                            alt="Temp Signature"
                                            className="max-h-[100px] mb-2 mx-auto"
                                        />
                                    </div>
                                )}
                            </div>
                        )}
                        {activeTab === 'draw' && (
                            <div>
                                <SignatureCanvas
                                    ref={sigCanvas}
                                    penColor="#FFF"
                                    canvasProps={{ className: 'signature-canvas bg-black w-full h-40 mb-4' }}
                                    onEnd={handleDrawSignature}
                                />
                                {tempSignature && (
                                    <div className="relative text-center">
                                        <img
                                            src={tempSignature}
                                            alt="Temp Signature"
                                            className="max-h-[100px] mb-2 mx-auto"
                                        />
                                    </div>
                                )}
                            </div>
                        )}
                        <div className="flex justify-end gap-4 mt-4">
                            <button onClick={handleReply} className="bg-[#282828] text-white px-4 py-2">
                                <Typography text="Clear" color="#FFF" size="14px" weight={500} />
                            </button>
                            <button onClick={handleApplySignature} className="bg-[#DF0F11] text-white px-4 py-2">
                                <Typography text="Apply" color="#FFF" size="14px" weight={500} />
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ReviewSign;